import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";
import {
  DEFAULT_WORKSPACE_ID,
  WorkspaceId,
} from "./workspaces";

// The active workspace is derived from the URL inside DashboardContainer, so
// this provider's only job is to remember the *previously rendered* workspace
// across page remounts (each admin page mounts its own DashboardContainer).
// That memory is what lets the sidebar play the Zen-style slide-in animation
// only when you actually switch workspaces — not on every navigation within a
// workspace.

interface WorkspaceContextValue {
  /** The workspace the sidebar last committed to (survives page remounts). */
  lastWorkspaceId: WorkspaceId;
  /** Called by DashboardContainer after it renders the active workspace. */
  commitWorkspace: (id: WorkspaceId) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const [lastWorkspaceId, setLastWorkspaceId] =
    useState<WorkspaceId>(DEFAULT_WORKSPACE_ID);

  const commitWorkspace = useCallback((id: WorkspaceId) => {
    setLastWorkspaceId((prev) => (prev === id ? prev : id));
  }, []);

  const value = useMemo(
    () => ({ lastWorkspaceId, commitWorkspace }),
    [lastWorkspaceId, commitWorkspace],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

// Stable fallback for the (shouldn't-happen) case of using the hook outside
// the provider: the sidebar simply never animates.
const FALLBACK_CONTEXT: WorkspaceContextValue = {
  lastWorkspaceId: DEFAULT_WORKSPACE_ID,
  commitWorkspace: () => {},
};

export function useWorkspace(): WorkspaceContextValue {
  return useContext(WorkspaceContext) ?? FALLBACK_CONTEXT;
}
